import _ from 'lodash';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';
import {
  AXIS_TICK_OFFSET,
} from './constants';
import { GenerateDecoratedCustomChartBarsFC } from './CustomChartBars';
import { ChartCustomTick } from './ChartCustomTick';

import {
  Container,
  ContainerHeader,
  QuestionOrder,
  Title,
  ContainerLabels,
  ChartContainer,
} from './styles';

const BAR_HEIGHT = 80; // fixed height bar
const PADDING = 20; // padding between bars

const RankingChart = (props) => {
  const {
    title,
    data,
    color,
  } = props;

  const {
    options,
    label,
    leftLabel,
    rightLabel,
    questionOrder,
    total,
  } = data || {}; // Ensure answers is not empty

  // Prepare chartData for each option
  const chartData = useMemo(() => options.map((option) => ({
    id: option.id,
    data: option.ranking,
  })), [options]);

  const yAxisPossibleTicks = [];

  const maxTick = Math.ceil(total / 10) * 10;
  yAxisPossibleTicks.push(..._.range(10, maxTick + 1, 10));

  // Add the total number of answers as the last tick if it's not already included
  if (!yAxisPossibleTicks.includes(total)) {
    yAxisPossibleTicks.push(total);
  }

  const totalBars = chartData.length; // Total number of bars
  const chartHeight = (totalBars * BAR_HEIGHT) + (totalBars * PADDING); // Total chart height

  return (
    <Container>
      <ContainerHeader>
        <QuestionOrder>{`Question ${questionOrder + 1}`}</QuestionOrder>
        <Title>{`${label} (${title})`}</Title>
      </ContainerHeader>
      {options.length === 0 ? (
        <div>No data available</div>
      ) : (
        chartData.map((optionData) => (
          <ChartContainer style={{ height: `${chartHeight}px` }} key={optionData.id}>
            <QuestionOrder>{options.find((qo) => qo.id === optionData.id).label}</QuestionOrder>
            <ResponsiveBar
              data={optionData.data.reverse()}
              enableGridX={false}
              enableGridY={false}
              axisLeft={{
                renderTick: (tick) => {
                  const {
                    x, y, lineY, value,
                  } = tick;
                  return (
                    <ChartCustomTick
                      label={`Ranked as #${value}`}
                      x={x}
                      y={y + lineY}
                    />
                  );
                },
              }}
              colors={[color]}
              axisBottom={{
                tickValues: yAxisPossibleTicks,
                legendPosition: 'middle',
                legendOffset: 40,
                tickSize: 0,
              }}
              margin={{
                top: 10,
                right: 60,
                bottom: 50,
                left: AXIS_TICK_OFFSET,
              }}
              gridXValues={yAxisPossibleTicks}
              indexBy="id"
              layout="horizontal"
              labelSkipWidth={6}
              layers={[
                'grid',
                'axes',
                GenerateDecoratedCustomChartBarsFC({
                  totalAnswerCount: total,
                }),
              ]}
              tooltip={() => <></>}
              maxValue={total}
              animate
              theme={{
                axis: {
                  domain: {
                    line: {
                      strokeWidth: 1,
                      stroke: '#777777',
                    },
                  },
                },
              }}
            />
          </ChartContainer>
        ))
      )}
      <ContainerLabels>
        <Title>{leftLabel}</Title>
        <Title>{rightLabel}</Title>
      </ContainerLabels>
    </Container>
  );
};

RankingChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  color: PropTypes.string.isRequired,
};

export default RankingChart;
